import React, { useEffect, useState, useCallback } from "react";
import { Loader2, Table2, CheckCircle2, AlertTriangle, Link2, ExternalLink, Trash2, KeyRound } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { api, formatDateTime } from "@/lib/api";
import { SheetTabsPanel } from "./SheetTabsPanel";

const REDIRECT_URI = `${process.env.REACT_APP_BACKEND_URL}/api/fees/sheets/oauth/callback`;

export default function SheetsSync() {
  const [cfg, setCfg] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [sheetUrl, setSheetUrl] = useState("");
  const [authUrl, setAuthUrl] = useState("");

  const load = useCallback(async () => {
    try {
      const [c, p] = await Promise.all([api.get("/fees/sheets/config"), api.get("/fees/sheets/preview")]);
      setCfg(c.data); setPreview(p.data);
    } catch { toast.error("Could not load Google Sheet settings"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [load]);

  const saveCreds = async () => {
    if (!clientId.trim() || !clientSecret.trim()) { toast.error("Enter both Client ID and Client secret"); return; }
    setBusy("creds");
    try { await api.post("/fees/sheets/credentials", { client_id: clientId.trim(), client_secret: clientSecret.trim() });
      setClientSecret(""); toast.success("Credentials saved securely"); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not save"); } finally { setBusy(""); }
  };
  const connect = async () => {
    setBusy("connect"); setAuthUrl("");
    try {
      const { data } = await api.post("/fees/sheets/connect-start", { redirect_uri: REDIRECT_URI });
      const w = window.open(data.url, "_blank");
      if (!w || w.closed || typeof w.closed === "undefined") {
        setAuthUrl(data.url);
        toast.error("Pop-up blocked — click the 'Open Google sign-in' link below.");
      } else {
        toast.info("Complete the Google consent in the new tab, then return here.");
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start Google connect"); }
    finally { setBusy(""); }
  };
  const saveSheet = async () => {
    if (!sheetUrl.trim()) { toast.error("Paste your Google Sheet link"); return; }
    setBusy("sheet");
    try { const { data } = await api.post("/fees/sheets/spreadsheet", { url: sheetUrl.trim() });
      toast.success(`Linked "${data.title}"`); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not open that sheet"); } finally { setBusy(""); }
  };
  const act = async (fn, key, ok) => { setBusy(key); try { await fn(); if (ok) toast.success(ok); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Action failed"); } finally { setBusy(""); } };

  if (loading) return <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-blue-900" /></div>;
  const cols = preview?.columns || [];
  const step = cfg?.connected && cfg?.sync_enabled ? 4 : cfg?.connected ? 3 : cfg?.authorized ? 3 : cfg?.has_credentials ? 2 : 1;

  return (
    <div className="space-y-4" data-testid="sheets-sync">
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h3 className="font-bold text-slate-900 flex items-center gap-2"><Table2 className="h-5 w-5" /> Google Sheet auto-sync</h3>
          <div className="flex items-center gap-2">
            <Badge className={cfg?.connected ? "bg-green-600" : "bg-slate-400"} data-testid="sheets-status">{cfg?.connected ? "Connected" : "Not connected"}</Badge>
            <Badge className={cfg?.sync_enabled ? "bg-blue-700" : "bg-amber-500"}>{cfg?.sync_enabled ? "Auto-sync ON" : "Auto-sync OFF"}</Badge>
          </div>
        </div>
        <p className="text-sm text-slate-600 mt-2">
          After you <b>approve</b> a payment, one row is added to the <b>Payments</b> tab of your sheet. Pending/rejected are never added.
          Duplicates are prevented by <b>UTR/Cheque Ref</b>; with no reference, a possible match is flagged in <b>Notes</b> for review — never auto-rejected.
          Sign-in is via Google — your password and Client secret are stored securely on the server, never shown again.
        </p>
      </div>

      {/* Step 1 — credentials */}
      <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3" data-testid="sheets-step-creds">
        <div className="flex items-center gap-2"><span className={`h-6 w-6 rounded-full text-white text-xs flex items-center justify-center ${step > 1 ? "bg-green-600" : "bg-blue-900"}`}>1</span>
          <h4 className="font-semibold text-slate-800 flex items-center gap-1"><KeyRound className="h-4 w-4" /> Google credentials</h4>
          {cfg?.has_credentials && <Badge className="bg-green-600">{cfg?.creds_source === "env" ? "From secure Secrets" : "Saved"}</Badge>}</div>
        {cfg?.creds_source === "env" ? (
          <div className="text-sm text-slate-600 rounded-lg bg-green-50 border border-green-200 p-3" data-testid="sheets-creds-env">
            <CheckCircle2 className="h-4 w-4 text-green-600 inline mr-1" /> Google credentials are provided securely via Emergent <b>Environment Secrets</b> — no secret is entered here or in chat. You can go straight to <b>Connect Google</b> below.
          </div>
        ) : (<>
          <div className="text-xs text-slate-500 rounded-lg bg-slate-50 border border-slate-200 p-2">
            The most secure option: add <b>GOOGLE_CLIENT_ID</b> and <b>GOOGLE_CLIENT_SECRET</b> in Emergent's <b>Secrets</b> panel and this step becomes one-click (no fields here). Otherwise, paste them below (stored encrypted server-side, never shown again). In Google Cloud Console → Credentials → create an <b>OAuth client ID (Web application)</b> and add this exact <b>Authorized redirect URI</b>:
            <div className="mt-1 font-mono text-[11px] bg-white border rounded px-2 py-1 break-all" data-testid="sheets-redirect-uri">{REDIRECT_URI}</div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label className="text-xs">Client ID</Label><Input value={clientId} onChange={(e) => setClientId(e.target.value)} placeholder="xxxx.apps.googleusercontent.com" data-testid="sheets-client-id" /></div>
            <div><Label className="text-xs">Client secret</Label><Input type="password" value={clientSecret} onChange={(e) => setClientSecret(e.target.value)} placeholder={cfg?.has_credentials ? "•••••• (saved — re-enter to change)" : "GOCSPX-…"} data-testid="sheets-client-secret" /></div>
          </div>
          <Button onClick={saveCreds} disabled={busy === "creds"} className="bg-blue-900" data-testid="sheets-save-creds">{busy === "creds" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save credentials securely"}</Button>
        </>)}
      </div>

      {/* Step 2 — connect */}
      <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3" data-testid="sheets-step-connect">
        <div className="flex items-center gap-2"><span className={`h-6 w-6 rounded-full text-white text-xs flex items-center justify-center ${cfg?.authorized ? "bg-green-600" : step >= 2 ? "bg-blue-900" : "bg-slate-300"}`}>2</span>
          <h4 className="font-semibold text-slate-800 flex items-center gap-1"><Link2 className="h-4 w-4" /> Connect Google account</h4>
          {cfg?.authorized && <Badge className="bg-green-600">Authorized</Badge>}</div>
        <p className="text-xs text-slate-500">You'll approve access on Google's own screen — no password is entered here.</p>
        <Button onClick={connect} disabled={!cfg?.has_credentials || busy === "connect"} className="bg-blue-900" data-testid="sheets-connect-btn">{busy === "connect" ? <Loader2 className="h-4 w-4 animate-spin" /> : (cfg?.authorized ? "Re-connect Google" : "Connect Google")}</Button>
        {authUrl && <a href={authUrl} target="_blank" rel="noreferrer" className="text-blue-700 text-sm font-semibold underline underline-offset-2 hover:text-blue-900 inline-flex items-center gap-1 ml-2" data-testid="sheets-auth-link">Open Google sign-in <ExternalLink className="h-3.5 w-3.5" /></a>}
        {cfg?.last_connect_error && <p className="text-[12px] text-red-700 bg-red-50 border border-red-200 rounded-lg p-2 mt-1" data-testid="sheets-connect-error">Last attempt: {cfg.last_connect_error}</p>}
      </div>

      {/* Step 3 — sheet + sample */}
      <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3" data-testid="sheets-step-sheet">
        <div className="flex items-center gap-2"><span className={`h-6 w-6 rounded-full text-white text-xs flex items-center justify-center ${cfg?.spreadsheet_id ? "bg-green-600" : cfg?.authorized ? "bg-blue-900" : "bg-slate-300"}`}>3</span>
          <h4 className="font-semibold text-slate-800">Link your sheet &amp; preview a sample row</h4></div>
        <div className="flex gap-2 flex-wrap">
          <Input value={sheetUrl} onChange={(e) => setSheetUrl(e.target.value)} placeholder="https://docs.google.com/spreadsheets/d/…" className="flex-1 min-w-[240px]" data-testid="sheets-url-input" disabled={!cfg?.authorized} />
          <Button onClick={saveSheet} disabled={!cfg?.authorized || busy === "sheet"} className="bg-blue-900" data-testid="sheets-save-url">{busy === "sheet" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Link sheet"}</Button>
        </div>
        {cfg?.sheet_link && (
          <div className="text-sm flex items-center gap-3 flex-wrap" data-testid="sheets-linked-info">
            <CheckCircle2 className="h-4 w-4 text-green-600" /> Linked: <b>{cfg.sheet_title || cfg.spreadsheet_id}</b>
            <a href={cfg.sheet_link} target="_blank" rel="noreferrer" className="text-blue-700 font-semibold underline underline-offset-2 hover:text-blue-900 inline-flex items-center gap-1" data-testid="sheets-open-link">Open sheet <ExternalLink className="h-3.5 w-3.5" /></a>
          </div>
        )}
        {cfg?.spreadsheet_id && (
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" onClick={() => act(() => api.post("/fees/sheets/sample-row"), "sample", "Sample row added to your sheet")} disabled={busy === "sample"} data-testid="sheets-add-sample">{busy === "sample" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add sample row to sheet"}</Button>
            <Button variant="outline" onClick={() => act(() => api.delete("/fees/sheets/sample-row"), "unsample", "Sample rows removed")} disabled={busy === "unsample"} data-testid="sheets-del-sample"><Trash2 className="h-4 w-4 mr-1" /> Remove sample rows</Button>
          </div>
        )}
      </div>

      {/* Step 4 — enable + backfill */}
      <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3" data-testid="sheets-step-enable">
        <div className="flex items-center gap-2"><span className={`h-6 w-6 rounded-full text-white text-xs flex items-center justify-center ${cfg?.sync_enabled ? "bg-green-600" : cfg?.connected ? "bg-blue-900" : "bg-slate-300"}`}>4</span>
          <h4 className="font-semibold text-slate-800">Turn on auto-sync (after you've reviewed)</h4></div>
        <div className="flex items-center gap-3">
          <Switch checked={!!cfg?.sync_enabled} disabled={!cfg?.connected || busy === "toggle"} data-testid="sheets-sync-toggle"
            onCheckedChange={(v) => act(() => api.post("/fees/sheets/sync-enabled", { enabled: v }), "toggle", v ? "Auto-sync ON — approved payments now sync" : "Auto-sync OFF")} />
          <span className="text-sm text-slate-600">{cfg?.sync_enabled ? "Approved payments are being added automatically." : "Off — no real payments are synced yet."}</span>
        </div>
        {cfg?.connected && <Button variant="outline" onClick={() => act(async () => { const { data } = await api.post("/fees/sheets/backfill"); toast.success(`Copied ${data.synced} payment(s) to the sheet · ${data.already} were already there`); }, "backfill")} disabled={busy === "backfill"} data-testid="sheets-backfill">{busy === "backfill" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Backfill existing approved payments"}</Button>}
        {cfg?.last_sync && <p className="text-xs text-green-700">Last sync: {formatDateTime(cfg.last_sync)}</p>}
        {cfg?.last_sync_error && <p className="text-[12px] text-red-700 bg-red-50 border border-red-200 rounded-lg p-2 flex items-start gap-2" data-testid="sheets-sync-error"><AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" /><span>Sync is failing: {cfg.last_sync_error}</span></p>}
      </div>

      <SheetTabsPanel cfg={cfg} reload={load} />

      {!cfg?.connected && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 flex items-start gap-2" data-testid="sheets-not-connected">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <span>This is a <b>preview</b> — nothing is connected and no live payment has been synced. The table below shows how an approved payment maps to your columns.</span>
        </div>
      )}

      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <h4 className="font-semibold text-slate-800 mb-2">Column mapping preview {preview?.is_sample && <span className="text-xs text-slate-400">(sample row)</span>}</h4>
        <div className="overflow-x-auto">
          <table className="text-xs border-collapse min-w-full" data-testid="sheets-preview-table">
            <thead><tr>{cols.map((c) => <th key={c} className="border border-slate-200 bg-slate-50 px-2 py-1 text-left font-semibold whitespace-nowrap">{c}</th>)}</tr></thead>
            <tbody>{(preview?.rows || []).map((r, i) => (
              <tr key={i} data-testid={`sheets-row-${i}`}>{cols.map((c) => <td key={c} className="border border-slate-200 px-2 py-1 whitespace-nowrap">{String(r[c] ?? "")}</td>)}</tr>
            ))}</tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
